import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  PermissionsGetManagePermissionPermissionsDocument,
  PermissionsGetManagePermissionPermissionsQuery,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useQuery } from 'modules/core/hooks/useQuery'

import { PERMISSION_GROUP_KIND_ADMIN } from 'modules/permissions/lib/constants'
import PermissionToggle from 'modules/permissions/pages/settings/PermissionGroupPage/List/Item/PermissionToggle'
import type PermissionGroup from 'modules/permissions/store/models/PermissionGroup'

type Permission =
  PermissionsGetManagePermissionPermissionsQuery['permissions']['permissions'][number]

interface Props {
  permissions: Permission[]
  permissionGroup: PermissionGroup
}

function DelegateContextlessPermissionsList({ permissions, permissionGroup }: Props) {
  const disabled =
    permissionGroup.attributes.kind === PERMISSION_GROUP_KIND_ADMIN ||
    !!permissionGroup.attributes.legacy_admin
  const permissionQuery = useQuery(PermissionsGetManagePermissionPermissionsDocument, {
    variables: {
      permissionsGroupId: permissionGroup.attributes.id,
    },
  })

  const afterToggle = async () => {
    await permissionQuery.refetch()
  }

  const half = Math.ceil(permissions.length / 2)
  const firstHalf = permissions.slice(0, half)
  const secondHalf = permissions.slice(half)

  return (
    <Box flexDirection="row" width="full" gap="s32">
      <Column
        permissions={firstHalf}
        permissionGroup={permissionGroup}
        disabled={disabled}
        afterToggle={afterToggle}
      />
      {secondHalf.length > 0 && (
        <Column
          permissions={secondHalf}
          permissionGroup={permissionGroup}
          disabled={disabled}
          afterToggle={afterToggle}
        />
      )}
    </Box>
  )
}

interface ColumnProps {
  permissions: Permission[]
  permissionGroup: PermissionGroup
  disabled: boolean
  afterToggle: () => Promise<void>
}

function Column({ permissions, permissionGroup, disabled, afterToggle }: ColumnProps) {
  return (
    <Box
      width="full"
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
      borderRadius={{
        all: 'abs008',
      }}
      padding="s24"
      gap="s16"
    >
      {permissions.map((permission) => (
        <ListItem
          key={permission.id}
          permission={permission}
          permissionGroup={permissionGroup}
          disabled={disabled}
          afterToggle={afterToggle}
        />
      ))}
    </Box>
  )
}

interface ListItemProps {
  permission: Permission
  permissionGroup: PermissionGroup
  disabled: boolean
  afterToggle: () => Promise<void>
}

const ListItem = observer(
  ({ permission, permissionGroup, disabled, afterToggle }: ListItemProps) => {
    return (
      <Box flexDirection="row" alignItems="center" gap="s32">
        <Box alignItems="flexStart" width="full" flexWrap="noWrap" overflowX="hidden" gap="s8">
          <Text.Normal weight="semibold" size="200" ellipsis>
            {permission.verbLabel}
          </Text.Normal>
          {permission.verbDescription && (
            <Text.Normal color="grey700" size="100">
              {permission.verbDescription}
            </Text.Normal>
          )}
        </Box>
        <Box alignItems="center" width="fit-content">
          <PermissionToggle
            disabled={disabled}
            permissionGroup={permissionGroup}
            permission={permission}
            verb={permission.verb}
            afterToggle={afterToggle}
          />
        </Box>
      </Box>
    )
  }
)

export default observer(DelegateContextlessPermissionsList)
